/**
 * Every call the UI can make, grouped to match backend/docs/API.md.
 *
 * The paths here are the source of truth. If the backend renames a route the
 * build should fail loudly rather than 404 at runtime, so each path is checked
 * against the generated document.
 */
import { api, publicApi, query, adoptSession, clearSession } from './client.js';

const auth = (path, options) => publicApi(path, options).then(adoptSessionResponse);

const adoptSessionResponse = (data) => {
  if (data?.accessToken) adoptSession(data);
  return data;
};

export const authApi = {
  signup: (body) => auth('/api/auth/signup', { method: 'POST', body }),
  verifyEmail: (body) => auth('/api/auth/verify-email', { method: 'POST', body }),
  resendVerification: (email) =>
    publicApi('/api/auth/resend-verification', { method: 'POST', body: { email } }),
  login: (body) => auth('/api/auth/login', { method: 'POST', body }),
  forgotPassword: (email) =>
    publicApi('/api/auth/forgot-password', { method: 'POST', body: { email } }),
  resetPassword: (body) => publicApi('/api/auth/reset-password', { method: 'POST', body }),
  refresh: (refreshToken) =>
    publicApi('/api/auth/refresh', { method: 'POST', body: { refreshToken } }),
  logout: () => api('/api/auth/logout', { method: 'POST', body: { refreshToken: localStorage.getItem('entry.refreshToken') } }),
  me: () => api('/api/auth/me'),
  updateMe: (body) => api('/api/auth/me', { method: 'PATCH', body }),
  schoolsForStaff: (params) => api(`/api/auth/schools${query(params)}`),
  createStaff: (body) => api('/api/auth/staff', { method: 'POST', body }),
  listStaff: (params) => api(`/api/auth/staff${query(params)}`)
};

export const schoolApi = {
  list: (params) => publicApi(`/api/schools${query(params)}`),
  detail: (slug) => publicApi(`/api/schools/${slug}`),
  reference: () => publicApi('/api/reference')
};

export const matchApi = {
  summary: (params) => api(`/api/match${query(params)}`),
  eligibility: (body) => api('/api/match/eligibility', { method: 'POST', body })
};

export const profileApi = {
  get: () => api('/api/profile'),
  save: (body) => api('/api/profile', { method: 'PUT', body })
};

export const resultsApi = {
  get: () => api('/api/results'),
  save: (body) => api('/api/results', { method: 'PUT', body }),
  clear: () => api('/api/results', { method: 'DELETE' }),
  /**
   * The slip is uploaded as a normal document first, then read by its id.
   * Nothing is saved to the student's record until they confirm the grades.
   */
  readSlip: (documentId) => api('/api/results/read-slip', { method: 'POST', body: { documentId } })
};

export const documentApi = {
  list: () => api('/api/documents'),
  upload: (file, kind) => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    return api('/api/documents', { method: 'POST', body: form });
  },
  remove: (id) => api(`/api/documents/${id}`, { method: 'DELETE' })
};

export const applicationApi = {
  list: (params) => api(`/api/applications${query(params)}`),
  detail: (id) => api(`/api/applications/${id}`),
  /** Exactly what the school receives, rendered before submit. */
  preview: (id) => api(`/api/applications/${id}/preview`),
  start: (body) => api('/api/applications/start', { method: 'POST', body }),
  saveAnswers: (id, body) => api(`/api/applications/${id}/answers`, { method: 'PUT', body }),
  chooseProgrammes: (id, body) => api(`/api/applications/${id}/programmes`, { method: 'PUT', body }),
  /** `key` is the school's own document key; the route scopes it to the school. */
  uploadDocument: (id, file, key) => {
    const form = new FormData();
    form.append('file', file);
    form.append('key', key);
    return api(`/api/applications/${id}/documents`, { method: 'POST', body: form });
  },
  review: (id) => api(`/api/applications/${id}/review`),
  submit: (id) => api(`/api/applications/${id}/submit`, { method: 'POST', body: {} }),
  withdraw: (id, reason) => api(`/api/applications/${id}/withdraw`, { method: 'POST', body: { reason } }),
  letter: (offerId) => api(`/api/applications/offers/${offerId}/letter`),
  respondToOffer: (offerId, body) => api(`/api/applications/offers/${offerId}/respond`, { method: 'POST', body })
};

export const paymentApi = {
  list: (params) => api(`/api/payments${query(params)}`),
  detail: (id) => api(`/api/payments/${id}`),
  initiate: (body) => api('/api/payments/initiate', { method: 'POST', body })
};

export const advisorApi = {
  brief: () => api('/api/advisor'),
  advice: (body) => api('/api/advisor/advice', { method: 'POST', body }),
  chat: (body) => api('/api/advisor/chat', { method: 'POST', body })
};

export const forumApi = {
  list: (params) => api(`/api/forums${query(params)}`),
  create: (body) => api('/api/forums', { method: 'POST', body }),
  detail: (id) => api(`/api/forums/${id}`),
  reply: (id, body) => api(`/api/forums/${id}/replies`, { method: 'POST', body }),
  vote: (id, body) => api(`/api/forums/${id}/vote`, { method: 'POST', body })
};

export const notificationApi = {
  list: (params) => api(`/api/notifications${query(params)}`),
  markRead: (body) => api('/api/notifications/read', { method: 'POST', body })
};

export const adminApi = {
  dashboard: () => api('/api/admin/dashboard'),
  applications: (params) => api(`/api/admin/applications${query(params)}`),
  application: (id) => api(`/api/admin/applications/${id}`),
  review: (id, body) => api(`/api/admin/applications/${id}/review`, { method: 'POST', body }),
  offer: (id, body) => api(`/api/admin/applications/${id}/offer`, { method: 'POST', body }),
  reject: (id, body) => api(`/api/admin/applications/${id}/reject`, { method: 'POST', body }),
  withdraw: (id, body) => api(`/api/admin/applications/${id}/withdraw`, { method: 'POST', body }),
  exportCsv: (params) => api(`/api/admin/applications/export.csv${query(params)}`),
  offers: (params) => api(`/api/admin/offers${query(params)}`),
  broadcast: (body) => api('/api/admin/broadcast', { method: 'POST', body }),
  dates: () => api('/api/admin/dates'),
  saveDates: (body) => api('/api/admin/dates', { method: 'PATCH', body }),
  programmes: (params) => api(`/api/admin/programmes${query(params)}`),
  createProgramme: (body) => api('/api/admin/programmes', { method: 'POST', body }),
  updateProgramme: (id, body) => api(`/api/admin/programmes/${id}`, { method: 'PATCH', body }),
  activity: (params) => api(`/api/admin/activity${query(params)}`)
};

export const serviceApi = {
  health: () => publicApi('/health'),
  ready: () => publicApi('/ready')
};

export { clearSession };
